<template>
    <div class="mt-8">
        <div class="font-semibold mb-2">{{ $t("reservedInvestments") }}</div>
        <div v-if="!items.length" class="text-sm">
            {{ $t("noReservedInvestments") }}
        </div>
        <div v-if="items.length">
            <div class="lg:hidden grid md:grid-cols-2 gap-3">
                <k-panel v-for="item in items" :key="item.uuid" class="bg-white px-2 py-2 border">
                    <div class="flex flex-row mb-4 pb-2 border-b items-center">
                        <div v-if="item.companyLogo" class="mr-2">
                            <img class="object-scale-down" style="height: 48px;"
                                 :src="`/api/guest/download/${item.companyLogo}`">
                        </div>
                        <div class="flex">
                            <div class="font-semibold text-sm">{{ item.companyName }}</div>
                        </div>
                    </div>
                    <k-panel-row>
                        <div slot="left">{{ $t("principalReserved") }}</div>
                        <div slot="right" class="text-right">
                            {{ item.principalReserved | currencyFormatWithoutDecimals(item.currencySymbol) }}
                        </div>
                    </k-panel-row>
                    <k-panel-row>
                        <div slot="left">{{ $t("interestRate") }}</div>
                        <div slot="right">{{ item.interestRate | numeralFormat('0.00') }}%</div>
                    </k-panel-row>
                    <k-panel-row>
                        <div slot="left">{{ $t("maturityDate") }}</div>
                        <div slot="right">{{ item.maturityDate | dateFormatShort }}</div>
                    </k-panel-row>
                    <k-panel-row>
                        <div slot="left">{{ $t("nextPaymentDate") }}</div>
                        <div slot="right">{{ item.nextPaymentDateOfSubscribed | dateFormatShort }}</div>
                    </k-panel-row>
                    <k-panel-row>
                        <div slot="left">{{ $t("nextPaymentAmount") }}</div>
                        <div slot="right">{{
                                item.nextPaymentAmountOfSubscribed | currencyFormatWithoutDecimals(item.currencySymbol)
                            }}
                        </div>
                    </k-panel-row>
                    <k-panel-row>
                        <div slot="left">
                            <a-popconfirm
                                title="Approve reservation?"
                                ok-text="Yes"
                                cancel-text="No"
                                @confirm="approve(item.uuid)"
                            >
                                <a-button type="primary" ghost @click.native.prevent="view(item.uuid)">
                                    {{ $t("approve") }}
                                </a-button>
                            </a-popconfirm>
                        </div>
                        <div slot="right">
                            <a-popconfirm
                                title="Cancel reservation?"
                                ok-text="Yes"
                                cancel-text="No"
                                @confirm="cancel(item.uuid)"
                            >
                                <a-button type="danger" ghost>
                                    {{ $t("cancel") }}
                                </a-button>
                            </a-popconfirm>
                        </div>
                    </k-panel-row>
                </k-panel>
            </div>
            <div class="hidden lg:block">
                <table class="table-auto bg-white w-full">
                    <thead>
                    <tr class="text-sm bg-gray-100">
                        <th class="border px-4 py-2" style="min-width: 400px; max-width: 400px; width: 400px;">
                            {{ $t("company") }}
                        </th>
                        <th class="border px-4 py-2 text-center"
                            style="min-width: 100px; max-width: 100px; width: 100px;">{{ $t("kildeRating") }}
                        </th>
                        <th class="border px-4 py-2 text-center">{{ $t("principalReserved") }}</th>
                        <th class="border px-4 py-2 text-center">{{ $t("interestRate") }}</th>
                        <th class="border px-4 py-2 text-center">{{ $t("maturityDate") }}</th>
                        <th class="border px-4 py-2 text-center">{{ $t("nextPayment") }}</th>
                        <th class="border px-4 py-2 text-center"></th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="item in items" :key="item.uuid" class="hover:bg-gray-100">
                        <td class="border px-4 py-4">
                            <div class="flex flex-row items-center">
                                <div v-if="item.companyLogo" class="mr-2">
                                    <img class="object-scale-down" style="width: 100px;"
                                         :src="`/api/guest/download/${item.companyLogo}`">
                                </div>
                                <div class="ml-2">
                                    <div class="font-semibold text-sm">{{ item.companyName }}</div>
                                    <div class="">
                                            <span v-for="(product, index) in item.productTypes"
                                                  class="text-xs text-gray-700">
                                                <span
                                                    v-if="index > 0">&nbsp;&bull;&nbsp;</span><span>{{ product }}</span>
                                            </span>
                                    </div>
                                    <div class="flex flex-row">
                                        <div class="mr-2 mt-1">
                                            <k-country-flags slot="right"
                                                             :countries="item.countries"></k-country-flags>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </td>
                        <td class="border px-4 py-4  whitespace-no-wrap text-center">
                            {{ item.creditRating }}
                        </td>
                        <td class="border px-4 py-4 whitespace-no-wrap text-right">
                            {{ item.principalReserved | currencyFormatWithoutDecimals(item.currencySymbol) }}
                        </td>
                        <td class="border px-4 py-4 whitespace-no-wrap text-right">
                            {{ item.interestRate | numeralFormat('0.00') }}%
                        </td>
                        <td class="border px-4 py-4 whitespace-no-wrap text-right">
                            {{ item.maturityDate | dateFormatShort }}
                        </td>
                        <td class="border px-4 py-4 whitespace-no-wrap text-right">
                            <div class="text-sm">
                                <div><span class="text-gray-700">{{ $t("date") }}:</span>
                                    {{ item.nextPaymentDateOfSubscribed | dateFormatShort }}
                                </div>
                                <div><span class="text-gray-700">{{ $t("amount") }}:</span>
                                    {{
                                        item.nextPaymentAmountOfSubscribed | currencyFormatWithoutDecimals(item.currencySymbol)
                                    }}
                                </div>
                            </div>
                        </td>
                        <td class="border px-4 py-4 whitespace-no-wrap text-center">
                            <router-link :to="`/tranche-invest/${item.uuid}`">
                                <a-popconfirm
                                    title="Approve reservation?"
                                    ok-text="Yes"
                                    cancel-text="No"
                                    @confirm="approve(item.uuid)"
                                >
                                    <a-button type="primary" ghost class="mr-2">
                                        {{ $t("approve") }}
                                    </a-button>
                                </a-popconfirm>
                            </router-link>
                            <a-popconfirm
                                title="Cancel reservation?"
                                ok-text="Yes"
                                cancel-text="No"
                                @confirm="cancel(item.uuid)"
                            >
                                <a-button type="danger" ghost>
                                    {{ $t("cancel") }}
                                </a-button>
                            </a-popconfirm>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        items: {
            type: Array,
            required: true
        }
    },
    methods: {
        async cancel(trancheUuid) {
            this.$emit('cancel', trancheUuid);
        },
        async approve(trancheUuid) {
            this.$emit('approve', trancheUuid);
        }
    }
};
</script>


